// owner-map: OP-08.spa
import { useState, useCallback, useEffect } from "react";
import type { Flags } from "../lib/flags.generated";
import { FLAG_DEFAULTS, flagsFromMe } from "../lib/flags.generated";
import { recordUiEvent } from "../lib/logRingBuffer";
import { setSharedLiveVintages } from "../lib/forecast/sharedLiveVintages.js";
import { groupsFromMe } from "../lib/telemetry/groups.js";
import { telemetryIdentityFromMe } from "../lib/telemetry/tabStart.js";
import { enableTelemetry } from "../services/telemetry.js";

/** The signed-in user as the session stores it (server/auth/routes.js) — `/api/me`'s `user`. */
export type MeUser = {
  sub?: string;
  email?: string;
  emailVerified?: boolean;
  name?: string;
  givenName?: string;
  familyName?: string;
  preferredUsername?: string;
};

/** The caller's own org from `/api/me` (`{id, name}` from meIdentity.js), or null. */
export type MeOrg = { id: string; name?: string | null };

/**
 * One current ToS document as `server/lib/tosManifest.js` describes it (`CURRENT_TOS`): the
 * fields TosGate / AuthShells / SettingsModal read on each entry.
 */
export type MeTosDoc = {
  name: string;
  displayName: string;
  version: string;
  url: string;
  relPath?: string;
};

/**
 * The `tos` block of a 200 `/api/me` answer (server/lib/tosBlock.js); absent when the gate is
 * off. `current` is the list of current documents, `accepted` the session's acceptance history.
 */
export type MeTos = {
  current?: MeTosDoc[];
  accepted?: Record<string, unknown>;
  isCurrent?: boolean;
  [key: string]: unknown;
};

/**
 * What `/api/me` sends, as this hook reads it — see `server/routes/me.js`. Every field is
 * optional: the 401 body carries no `user`/`preferences`/`tos`, the anonymous and local-dev
 * answers omit `gcpProject`, and the deployment flags (`...flags`) are read through
 * `flagsFromMe`, which tolerates any missing key.
 */
export type MePayload = {
  user?: MeUser | null;
  org?: MeOrg | null;
  groups?: unknown[] | null;
  groupsDiagnostic?: Record<string, unknown> | null;
  preferences?: Record<string, unknown> | null;
  authEnabled?: boolean;
  persistenceEnabled?: boolean;
  telemetryStreamEnabled?: boolean;
  sharedLiveVintagesEnabled?: unknown;
  sourceSpace?: string | null;
  spaceTier?: string | null;
  gcpProject?: string;
  debugMenuEnabled?: boolean;
  tos?: MeTos | null;
  [flag: string]: unknown;
};

// Base browser-tab title as baked into index.html (%BRAND_TITLE% → e.g. "Sayer"),
// captured once at import BEFORE we ever mutate document.title, so re-applying on
// each /api/me is idempotent (no repeated suffixes on the visibility refetch).
const BASE_TITLE = typeof document !== "undefined" ? document.title : "Sayer";

/**
 * The tab-title suffix for a deployment (from /api/me `sourceSpace` and `spaceTier`), so
 * non-prod tabs are distinguishable at a glance. Runtime-driven (SOURCE_SPACE, SPACE_TIER),
 * so the same build shows the right label in every environment. A customer-tier space is
 * that customer's production, so it carries no suffix, like prod.
 */
export function envTabTitleSuffix(
  sourceSpace: string | null | undefined,
  spaceTier: string | null | undefined,
): string {
  if (spaceTier === "customer") return "";
  const v = String(sourceSpace || "").toLowerCase();
  if (v === "prod") return "";
  if (v === "integration") return " - Integration";
  if (v === "staging") return " - Staging";
  if (!v || v.startsWith("local")) return " - Local";
  return " - " + v.charAt(0).toUpperCase() + v.slice(1);
}

function applyEnvTabTitle(me: MePayload): void {
  if (typeof document === "undefined") return;
  document.title = BASE_TITLE + envTabTitleSuffix(me.sourceSpace, me.spaceTier);
}

export function useAuthSession({
  setAuthEnabledFlag,
}: {
  setAuthEnabledFlag: (enabled: boolean) => void;
}) {
  // authEnabled is true when the server enforces auth (the public Zitadel-gated deploy)
  // and false in legacy/dev mode without ZITADEL_ISSUER set.
  const [me, setMe] = useState<MeUser | null>(null);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [persistenceEnabled, setPersistenceEnabled] = useState(false); // /api/me reports DB-backed persistence
  // The deployment flags, one object by client name, generated from server/flags/registry.js
  // (app/src/lib/flags.generated.ts): each is the `chatboard-*` GOFF flag resolved server-side
  // in /api/me; the coercion, the default and what it gates are the registry entry's. The
  // defaults are the fail-safe state (relay-down / pre-fetch / a failed /api/me); the consumers
  // read the flags flat off this hook's return.
  //
  // They live HERE rather than on `me` because `me` is /api/me's `user` object
  // (`setMe(d.user || null)` below), and the deployment flags are SIBLINGS of `user` in
  // that body.
  const [flags, setFlags] = useState<Flags>(FLAG_DEFAULTS);
  // GCP project holding this deployment's traces, for the debug console deep links.
  // EXPOSE_DEBUG_UI-gated server-side, so "" in prod and off-GCP dev.
  const [gcpProject, setGcpProject] = useState("");
  // Whether the triple-click debug menu may open for this user: wherever `debugUIEnabled` is,
  // plus a verified staff account on a plane without it (server/lib/debugMenu.js). It opens the
  // menu only; anything that calls a debug route still keys on `debugUIEnabled`.
  const [debugMenuAllowed, setDebugMenuAllowed] = useState(false);
  // The caller's own org — `{id, name}` from /api/me's meIdentity.js, or null when the
  // caller has no org. ShareDialog needs `id` to CONSTRUCT the org-lane write entry (never
  // read off a loaded share — see lib/shareDialog.js's own header).
  const [org, setOrg] = useState<MeOrg | null>(null);
  // Per-user account preferences jsonb from /api/me. setPreferences lets callers
  // optimistically reflect a PATCH /api/me/preferences.
  const [preferences, setPreferences] = useState<Record<string, unknown>>({});
  const [tos, setTos] = useState<MeTos | null>(null); // null = no tos block (legacy/inactive/unauth)
  const [tosPreview, setTosPreview] = useState<MeTos | null>(null); // debug: non-null renders TosGate as a dismissible preview overlay
  const [authLoading, setAuthLoading] = useState(true);
  const fetchMe = useCallback(
    async ({ isMount = false }: { isMount?: boolean } = {}) => {
      // `chatboard-telemetry-stream` and its sub-flag groups go to the telemetry service
      // at once, not from an effect after the render: what this answer records next
      // (ui.auth.identity) must already see the stream on, or a disabled → enabled answer
      // acknowledges it unsent. Until the first answer the service buffers; fail-closed.
      const applyTelemetry = (d: MePayload) => {
        enableTelemetry(
          d.telemetryStreamEnabled === true,
          groupsFromMe(d),
          telemetryIdentityFromMe(d),
        );
      };
      // Everything a /api/me body carries besides identity, the same way for the 401 and the
      // 200 answer: the registry's flags, the raw shared-live-vintages switch (a module
      // setter, fail-open), the disclosed project and the telemetry stream + groups.
      const applyFlags = (d: MePayload) => {
        setFlags(flagsFromMe(d));
        setSharedLiveVintages(d.sharedLiveVintagesEnabled);
        setGcpProject(typeof d.gcpProject === "string" ? d.gcpProject : "");
        // ORed with `debugUIEnabled` here too, so an answer without the new field (an older
        // server, a test double) never closes a menu the deployment exposes.
        setDebugMenuAllowed(d.debugMenuEnabled === true || d.debugUIEnabled === true);
        applyTelemetry(d);
      };
      try {
        const r = await fetch("/api/me", { credentials: "include" });
        if (r.status === 401) {
          const d = (await r.json().catch(() => ({}))) as MePayload;
          applyEnvTabTitle(d);
          setAuthEnabled(d.authEnabled !== false);
          setAuthEnabledFlag(d.authEnabled !== false);
          setPersistenceEnabled(!!d.persistenceEnabled);
          applyFlags(d);
          setOrg(d.org || null);
          setMe(null);
          setPreferences({});
          setTos(null);
        } else {
          const d = (await r.json()) as MePayload;
          applyEnvTabTitle(d);
          setAuthEnabled(!!d.authEnabled);
          setAuthEnabledFlag(!!d.authEnabled);
          setPersistenceEnabled(!!d.persistenceEnabled);
          applyFlags(d);
          setOrg(d.org || null);
          setMe(d.user || null);
          setPreferences(d.preferences || {});
          setTos(d.tos || null);
          // Logs the session's org and team count to bug bundles. `groupsDiagnostic` is
          // present ONLY when the metadata claim carried keys but not `groups` — the
          // misconfiguration that looks like "no teams" from outside. Key names only; a
          // metadata VALUE never leaves the server, so nothing sensitive can reach a bundle
          // through here.
          recordUiEvent({
            event: "ui.auth.identity",
            orgId: d.org?.id || null,
            teamCount: Array.isArray(d.groups) ? d.groups.length : 0,
            ...(d.groupsDiagnostic && { groupsDiagnostic: d.groupsDiagnostic }),
          });
        }
      } catch (_e) {
        setAuthEnabled(false);
        setAuthEnabledFlag(false);
        setPersistenceEnabled(false);
        // Every flag back to its fail-safe default (the ones that ship ON stay on — never carry
        // a prior session's OFF), the stream off.
        setFlags(FLAG_DEFAULTS);
        setDebugMenuAllowed(false);
        setSharedLiveVintages(true);
        enableTelemetry(false);
        setOrg(null);
        setMe(null);
        setPreferences({});
        setTos(null);
      } finally {
        if (isMount) setAuthLoading(false);
      }
      // setAuthEnabledFlag is a stable module-level function passed in as an arg; listing it
      // satisfies exhaustive-deps without changing fetchMe's (stable) identity.
    },
    [setAuthEnabledFlag],
  );
  useEffect(() => {
    fetchMe({ isMount: true });
  }, [fetchMe]);
  // Re-fetch /api/me when the tab becomes visible (e.g. user returns after
  // signing in on another tab, or the ToS manifest was updated).
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === "visible") fetchMe();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchMe]);
  // Light /api/me check used before actions that should be gated by the ToS.
  // Returns true when the caller should proceed, false when either the ToS
  // gate has been raised (isCurrent===false) or the session has expired (401).
  // Network errors are treated as "proceed" so a momentary outage never blocks
  // the user — the action's own /api call will surface any real failure.
  const guardForTos = useCallback(async () => {
    try {
      const r = await fetch("/api/me", { credentials: "include" });
      if (r.status === 401) {
        // Session expired. Delegate to fetchMe so authEnabled is restored from
        // the 401 response body (a prior fetchMe network-error catch may have
        // wrongly knocked it to false; the sign-in render fork only fires when
        // authEnabled && !me).
        await fetchMe();
        return false;
      }
      if (r.status === 200) {
        const d = (await r.json()) as MePayload;
        if (d.tos && d.tos.isCurrent === false) {
          setTos(d.tos);
          return false;
        }
      }
    } catch (_) {
      /* network error — let the caller proceed */
    }
    return true;
  }, [fetchMe]);
  return {
    me,
    preferences,
    setPreferences,
    authEnabled,
    persistenceEnabled,
    ...flags,
    gcpProject,
    debugMenuAllowed,
    org,
    tos,
    setTos,
    tosPreview,
    setTosPreview,
    authLoading,
    fetchMe,
    guardForTos,
  };
}
